import Decimal from 'decimal.js';

/**
 * Accounting v1 — shared constants + pure money/date helpers.
 *
 * This file is the seam the internal accounting plan (docs/plans/
 * accounting-build-plan.md) reserves for the Phase 4 posting catalog; today it
 * carries the document-number series registry, the VAT-inclusive
 * decomposition, and the AP aging bucket math.
 */

/**
 * Canonical document-number series. The DB row in `doc_number_series` is
 * created on first mint by `nextDocNumber()` (apps/api services/doc-numbers)
 * from these definitions — change a prefix/pad here BEFORE the first mint of
 * that series; after that the DB row wins.
 */
export const DOC_SERIES = {
  /** Consign-Out note ("Consignment Note"). */
  CN: { prefix: 'CN-', pad: 5 },
  /** Consignment return. */
  RTN: { prefix: 'RTN-', pad: 5 },
  /** Consignment sell-through receivable ("Billing Statement"). */
  AR: { prefix: 'AR-', pad: 5 },
  /** Consignment payment ("Payment Record"). */
  PAY: { prefix: 'PAY-', pad: 5 },
  /** Stockist consign-out note (Track 2A; branch-keyed twin of CN). */
  SCN: { prefix: 'SCN-', pad: 5 },
  /** Stockist sell-through receivable ("Billing Statement"). */
  SAR: { prefix: 'SAR-', pad: 5 },
  /** Stockist consignment return. */
  SRTN: { prefix: 'SRTN-', pad: 5 },
  /** Stockist consignment payment ("Payment Record"). */
  SCPAY: { prefix: 'SCPAY-', pad: 5 },
  /** Supplier payment. */
  SPAY: { prefix: 'SPAY-', pad: 5 },
  /** Return to supplier (goods sent back; AP/GRNI credit). */
  RTS: { prefix: 'RTS-', pad: 5 },
  /** Supplier refund — cash the supplier sent back against an RTS credit. */
  SREF: { prefix: 'SREF-', pad: 5 },
  /** Expense record. */
  EXP: { prefix: 'EXP-', pad: 5 },
  /** Funds document: manual deposit / withdrawal / transfer between accounts. */
  FT: { prefix: 'FT-', pad: 5 },
  /** General-ledger journal entry (Phase 4 lean GL). */
  JE: { prefix: 'JE-', pad: 5 },
  /** R2 customer master. */
  CUST: { prefix: 'CUST-', pad: 5 },
  /**
   * R2 Phase D credit refund — unapplied customer credit handed back as money.
   * Note there is deliberately no `CM` key here: the standalone credit memo
   * shares the GLOBAL `CM` counter with `orderDocSeries`, and declaring it in
   * both places would leave the key with two owners (D17).
   */
  CRF: { prefix: 'CRF-', pad: 5 },
} as const;

export type DocSeriesKey = keyof typeof DOC_SERIES;

/**
 * Consignment permission codes — the ONE place these strings are written.
 *
 * Accounting v1 shipped a single `consignment.write` switch; it is retired
 * (kept as a non-grantable row for rollback, granted by nothing) in favour of
 * one code per document type, so a tenant can let a clerk bill without also
 * letting them void. `print` and `export` are ADVISORY — both are produced
 * client-side from payloads `consignment.read` already returns, so they hide
 * affordances rather than guard data. Never re-declare these literals; import
 * them (the cashier/admin SPAs and the ACCESS_TREE all read from here).
 */
export const CONSIGNMENT_PERMS = {
  read: 'consignment.read',
  consignOut: 'consignment.consign_out',
  bill: 'consignment.bill',
  receivePayment: 'consignment.receive_payment',
  return: 'consignment.return',
  void: 'consignment.void',
  print: 'consignment.print',
  export: 'consignment.export',
} as const;

/** The per-action codes that replaced `consignment.write` (seed + migration). */
export const CONSIGNMENT_ACTION_PERMS = [
  CONSIGNMENT_PERMS.consignOut,
  CONSIGNMENT_PERMS.bill,
  CONSIGNMENT_PERMS.receivePayment,
  CONSIGNMENT_PERMS.return,
  CONSIGNMENT_PERMS.void,
  CONSIGNMENT_PERMS.print,
  CONSIGNMENT_PERMS.export,
] as const;

/** Retired: no route reads it; the 0162 backfill moved every holder forward. */
export const LEGACY_CONSIGNMENT_WRITE_PERM = 'consignment.write';

/**
 * Stockist-consignment permission codes (Track 2A) — the ONE place these
 * strings are written. Mirrors `CONSIGNMENT_PERMS` action-for-action (born
 * per-action; there is no legacy single switch here). `print` and `export` are
 * ADVISORY for the same reason as the consignment ones: produced client-side
 * from payloads `stockist_consignment.read` already returns. Never re-declare
 * these literals; import them (SPAs, ACCESS_TREE and routes all read from
 * here).
 */
export const STOCKIST_CONSIGNMENT_PERMS = {
  read: 'stockist_consignment.read',
  consignOut: 'stockist_consignment.consign_out',
  bill: 'stockist_consignment.bill',
  receivePayment: 'stockist_consignment.receive_payment',
  return: 'stockist_consignment.return',
  void: 'stockist_consignment.void',
  print: 'stockist_consignment.print',
  export: 'stockist_consignment.export',
} as const;

/**
 * R2 receivables permission codes — the ONE place these strings are written.
 * `customer.read`/`customer.write` gate the customer master (not accounting-
 * only: a customer is commerce data), `payment_terms.write` gates the shared
 * terms vocabulary, and `view.accounting.ar` is the nav/view leaf for the
 * Receivables pages — the first `view.accounting.*` sub-leaf, so it needs an
 * ACCESS_TREE node, a seed entry, the route wrapper and a sidebar leaf, not
 * just a requirePerm. Shape follows STOCKIST_CONSIGNMENT_PERMS.
 */
export const AR_PERMS = {
  customerRead: 'customer.read',
  customerWrite: 'customer.write',
  paymentTermsWrite: 'payment_terms.write',
  viewAr: 'view.accounting.ar',
  /** Create/edit an AR billing (both kinds). Declared in 0182; read from Phase C. */
  invoiceWrite: 'ar.invoice.write',
  /** Void any AR document. Separate from `invoiceWrite`, like `consignment.void`. */
  void: 'ar.void',
  /**
   * Phase D: record a payment, reallocate one, apply a customer's credit, or
   * unapply an application. Declared in 0183.
   */
  receiptWrite: 'ar.receipt.write',
  /**
   * Phase D: hand unapplied credit back as money (CRF-). Separate from
   * `receiptWrite` because it MOVES CASH OUT — an operator who may record
   * collections is not thereby allowed to disburse.
   */
  creditRefundWrite: 'ar.credit_refund.write',
} as const;

/** Customer kinds. `member` is the bridged-to-a-user case (D8). */
export const CUSTOMER_KINDS = ['member', 'individual', 'business'] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];

/**
 * How a payment term turns a document date into a due date. `net` = date +
 * days; `eom` = date + days, then round to the end of THAT month (tenant
 * timezone). The math itself lands in Phase B's `computeDueDate`.
 */
export const PAYMENT_TERM_KINDS = ['net', 'eom'] as const;
export type PaymentTermKind = (typeof PAYMENT_TERM_KINDS)[number];

/**
 * The terms every tenant starts with (seeded `ON CONFLICT (code) DO NOTHING`,
 * so a tenant's edits and additions survive re-seeding). `due_on_receipt` is
 * the tenant default because it is the only one that is never wrong — a term
 * the bookkeeper did not choose should not silently extend credit.
 */
export const PAYMENT_TERMS_SEED = [
  { code: 'due_on_receipt', name: 'Due on receipt', days: 0, kind: 'net', sortOrder: 10 },
  { code: 'net7', name: 'Net 7', days: 7, kind: 'net', sortOrder: 20 },
  { code: 'net15', name: 'Net 15', days: 15, kind: 'net', sortOrder: 30 },
  { code: 'net30', name: 'Net 30', days: 30, kind: 'net', sortOrder: 40 },
  { code: 'net60', name: 'Net 60', days: 60, kind: 'net', sortOrder: 50 },
  { code: 'eom', name: 'End of month', days: 0, kind: 'eom', sortOrder: 60 },
] as const;

// ── Cash/Bank register (funds subledger) ────────────────────────────────────

/**
 * What kind of thing a company account is. `ewallet` exists because the
 * payment vocabularies already carry `gcash`, and treating GCash/Maya as bank
 * accounts is the roadmap's stated position.
 */
export const FUNDS_ACCOUNT_KINDS = ['cash', 'bank', 'cheque', 'ewallet'] as const;
export type FundsAccountKind = (typeof FUNDS_ACCOUNT_KINDS)[number];

/** Manual money movements the register itself originates (doc series FT-). */
export const FUNDS_DOC_KINDS = ['deposit', 'withdrawal', 'transfer'] as const;
export type FundsDocKind = (typeof FUNDS_DOC_KINDS)[number];

/**
 * What produced a `funds_transactions` row. These double as the GL poster's
 * `source_type` when Phase 4 lands, which is why they name the DOCUMENT rather
 * than the direction.
 */
export const FUNDS_SOURCE_TYPES = [
  'opening_balance',
  'supplier_payment',
  // Money IN from a supplier against an RTS credit. Like `supplier_payment` it
  // carries a user-picked account and no branch, so it is deliberately NOT in
  // FUNDS_AUTOPOST_SOURCES (there is no mapping rule to resolve).
  'supplier_refund',
  'expense',
  'consignment_receipt',
  // Settlement of a STOCKIST's consignment receivable (SCPAY-). Carries a
  // user-picked account like `consignment_receipt`, so deliberately NOT in
  // FUNDS_AUTOPOST_SOURCES.
  'stockist_consignment_receipt',
  // Money OUT: a customer's unapplied credit handed back (CRF-). Same reason as
  // its siblings above — the account is chosen by whoever issues the refund, so
  // there is no mapping rule to resolve and it is NOT in FUNDS_AUTOPOST_SOURCES.
  'ar_credit_refund',
  'manual_deposit',
  'manual_withdrawal',
  'transfer',
  // Auto-posted by hooks through `funds_account_mappings` (see
  // FUNDS_AUTOPOST_SOURCES). Each names the document that moved the money.
  'pos_sale',
  'gateway_payment',
  'manual_payment',
  'cod_collection',
  'wallet_topup',
  'member_payout',
  'merchant_payout',
  'creator_payout',
] as const;
export type FundsSourceType = (typeof FUNDS_SOURCE_TYPES)[number];

/**
 * Sources the register receives AUTOMATICALLY: a `(source, code, branch)` rule
 * in `funds_account_mappings` names the account and the money service posts
 * when the movement becomes final. The `source` column of a rule is one of
 * these and is also the `source_type` of the rows it produces. Direction is a
 * property of the source, never of the rule.
 */
export const FUNDS_AUTOPOST_SOURCES = [
  'pos_sale',
  'gateway_payment',
  'manual_payment',
  'cod_collection',
  'wallet_topup',
  'member_payout',
  'merchant_payout',
  'creator_payout',
] as const;
export type FundsAutopostSource = (typeof FUNDS_AUTOPOST_SOURCES)[number];

export const FUNDS_AUTOPOST_DIRECTION: Record<FundsAutopostSource, 'in' | 'out'> = {
  pos_sale: 'in',
  gateway_payment: 'in',
  manual_payment: 'in',
  cod_collection: 'in',
  wallet_topup: 'in',
  member_payout: 'out',
  merchant_payout: 'out',
  creator_payout: 'out',
};

/** Editor-facing names for the rule's source picker. */
export const FUNDS_AUTOPOST_LABELS: Record<FundsAutopostSource, string> = {
  pos_sale: 'POS sales (cash / card tenders)',
  gateway_payment: 'Online gateway payments',
  manual_payment: 'Manual payments (proof approved / recorded by admin)',
  cod_collection: 'Cash on Delivery collections',
  wallet_topup: 'Wallet & AI-credit top-ups',
  member_payout: 'Member cash-outs',
  merchant_payout: 'Merchant cash-outs',
  creator_payout: 'Creator cash-outs',
};

/**
 * Sources whose documents carry no branch. A branch-bound till can never serve
 * them (the register's branch rule would reject every posting), so the rule
 * editor refuses the pairing up front.
 */
export const FUNDS_BRANCHLESS_SOURCES: readonly FundsAutopostSource[] = [
  'wallet_topup',
  'member_payout',
  'merchant_payout',
  'creator_payout',
];

/**
 * Forward types an ORDER can have posted under (`source_ref` = the order id).
 * A refund/cancel reverses whichever of these actually posted.
 */
export const ORDER_FUNDS_SOURCES: readonly FundsAutopostSource[] = [
  'gateway_payment',
  'manual_payment',
  'cod_collection',
];

/** A reversal keeps the original `source_ref` and flips the direction. */
export type FundsVoidSourceType = `${FundsSourceType}_void`;
export type FundsRowSourceType = FundsSourceType | FundsVoidSourceType;
export const fundsVoidSourceType = (t: FundsSourceType): FundsVoidSourceType => `${t}_void`;

export const FUNDS_SOURCE_LABELS: Record<FundsRowSourceType, string> = {
  opening_balance: 'Opening balance',
  // Unreachable today — an opening balance is immutable, and a correction is a
  // new FT- document. Present so the map stays total over the type.
  opening_balance_void: 'Opening balance (void)',
  supplier_payment: 'Supplier payment',
  supplier_payment_void: 'Supplier payment (void)',
  supplier_refund: 'Supplier refund',
  supplier_refund_void: 'Supplier refund (void)',
  expense: 'Expense',
  expense_void: 'Expense (void)',
  consignment_receipt: 'Consignment payment',
  consignment_receipt_void: 'Consignment payment (void)',
  stockist_consignment_receipt: 'Stockist consignment payment',
  stockist_consignment_receipt_void: 'Stockist consignment payment (void)',
  ar_credit_refund: 'Customer credit refund',
  ar_credit_refund_void: 'Customer credit refund (void)',
  manual_deposit: 'Deposit',
  manual_deposit_void: 'Deposit (void)',
  manual_withdrawal: 'Withdrawal',
  manual_withdrawal_void: 'Withdrawal (void)',
  transfer: 'Transfer',
  transfer_void: 'Transfer (void)',
  pos_sale: 'POS sale',
  pos_sale_void: 'POS sale (refund)',
  gateway_payment: 'Gateway payment',
  gateway_payment_void: 'Gateway payment (refund)',
  manual_payment: 'Manual payment',
  manual_payment_void: 'Manual payment (refund)',
  cod_collection: 'COD collection',
  cod_collection_void: 'COD collection (refund)',
  wallet_topup: 'Wallet top-up',
  // Unreachable today — an approved top-up is never reversed. Present so the
  // map stays total over the type.
  wallet_topup_void: 'Wallet top-up (void)',
  member_payout: 'Member cash-out',
  member_payout_void: 'Member cash-out (void)',
  merchant_payout: 'Merchant cash-out',
  merchant_payout_void: 'Merchant cash-out (void)',
  creator_payout: 'Creator cash-out',
  creator_payout_void: 'Creator cash-out (void)',
};

/** Manage accounts and mint/void manual funds documents (treasury authority). */
export const FUNDS_ACCOUNT_WRITE_PERM = 'funds_account.write';

// ── Return to Supplier (RTS-) ───────────────────────────────────────────────

/** Record / void returns of goods to suppliers. */
export const SUPPLIER_RETURN_WRITE_PERM = 'supplier_return.write';

export const SUPPLIER_RETURN_REASONS = [
  'damaged',
  'wrong_item',
  'excess',
  'quality',
  'other',
] as const;
export type SupplierReturnReason = (typeof SUPPLIER_RETURN_REASONS)[number];
export const SUPPLIER_RETURN_REASON_LABELS: Record<SupplierReturnReason, string> = {
  damaged: 'Damaged',
  wrong_item: 'Wrong item',
  excess: 'Excess / over-delivery',
  quality: 'Quality issue',
  other: 'Other',
};

/**
 * What an RTS relieves, frozen at creation: `bill_credit` (linked to a posted
 * bill → AP), `grni_relief` (received, not yet billed → GRNI), `supplier_credit`
 * (ad-hoc, no PO/bill → AP; the supplier owes us).
 */
export const SUPPLIER_RETURN_CREDIT_KINDS = [
  'bill_credit',
  'grni_relief',
  'supplier_credit',
] as const;
export type SupplierReturnCreditKind = (typeof SUPPLIER_RETURN_CREDIT_KINDS)[number];
export const SUPPLIER_RETURN_CREDIT_KIND_LABELS: Record<SupplierReturnCreditKind, string> = {
  bill_credit: 'Bill credit',
  grni_relief: 'GRNI reversal',
  supplier_credit: 'Supplier credit',
};

/**
 * Settlement of a supplier bill (internal plan D31): `settled = paid + credit`;
 * `paid` on EXACT equality with the total, `partial` once anything is settled,
 * else `open`. The ONE status rule shared by payment create/void and RTS
 * create/void — never re-derive it inline.
 */
export function supplierBillOutstanding(
  total: Decimal.Value,
  amountPaid: Decimal.Value,
  creditApplied: Decimal.Value,
): Decimal {
  return new Decimal(total).minus(amountPaid).minus(creditApplied);
}

export function supplierBillStatus(
  total: Decimal.Value,
  amountPaid: Decimal.Value,
  creditApplied: Decimal.Value,
): 'open' | 'partial' | 'paid' {
  const settled = new Decimal(amountPaid).plus(creditApplied);
  if (settled.equals(total)) return 'paid';
  return settled.gt(0) ? 'partial' : 'open';
}

// ── Lean GL (Phase 4) ───────────────────────────────────────────────────────

/** Posting manual journal entries + reversing entries — moves book values. */
export const ACCOUNTING_JOURNAL_WRITE_PERM = 'accounting.journal.write';
/** Renaming / deactivating / reordering Chart of Accounts entries. */
export const ACCOUNTING_COA_WRITE_PERM = 'accounting.coa.write';

export const GL_ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'] as const;
export type GlAccountType = (typeof GL_ACCOUNT_TYPES)[number];

/** Statement-side labels, in balance-sheet-then-P&L order. */
export const GL_ACCOUNT_TYPE_LABELS: Record<GlAccountType, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expenses',
};

/** The debit-natural types; the rest are credit-natural. */
export const GL_DEBIT_NATURAL_TYPES: readonly GlAccountType[] = ['asset', 'expense'];

/**
 * The seeded Chart of Accounts — the ONE place the default codes are written.
 * Codes are STABLE (posting rules and `funds_accounts.gl_account_code` /
 * `expense_categories.gl_account_code` key on them); names here are only the
 * seed defaults — tenants rename freely in the CoA editor, so always display
 * the DB row's name, never these.
 *
 * Mirrored as data in migration 0169 and packages/db seed.ts ("every data
 * seed exists twice"). 1060–1099 are deliberately absent: the GL poster mints
 * one bank-family sub-account per bank/cheque/ewallet funds account lazily.
 */
export const GL_ACCOUNTS = {
  '1000': { name: 'Cash on Hand — POS', type: 'asset' },
  '1010': { name: 'Cash in Bank', type: 'asset' },
  '1020': { name: 'Gateway Clearing', type: 'asset' },
  '1030': { name: 'AR — COD', type: 'asset' },
  '1040': { name: 'AR — Consignment', type: 'asset' },
  // What a STOCKIST owes the house for consigned goods they have sold through.
  // Deliberately NOT 2430 Stockist Payable, which is the opposite direction (the
  // house owing a stockist under the older Order-by-Area module).
  '1045': { name: 'AR — Stockist', type: 'asset' },
  // R2: general (non-consignment) receivables. Consignment stays on 1040 so an
  // existing tenant's trial balance does not move when general invoicing lands.
  '1050': { name: 'AR — Trade', type: 'asset' },
  '1100': { name: 'Merchandise Inventory', type: 'asset' },
  '1105': { name: 'Consigned Goods', type: 'asset' },
  '1200': { name: 'Input VAT', type: 'asset' },
  '2000': { name: 'AP — Trade', type: 'liability' },
  '2010': { name: 'Goods Received Not Invoiced', type: 'liability' },
  '2100': { name: 'Member Wallet Liability', type: 'liability' },
  '2110': { name: 'Merchant Wallet Liability', type: 'liability' },
  '2115': { name: 'Creator Wallet Liability', type: 'liability' },
  '2120': { name: 'GC Scrip Liability', type: 'liability' },
  '2130': { name: 'Coin Scrip Liability', type: 'liability' },
  '2200': { name: 'Output VAT Payable', type: 'liability' },
  '2210': { name: 'Withholding Tax Payable', type: 'liability' },
  '2300': { name: 'Customer Deposits (Unearned)', type: 'liability' },
  // R2: unapplied customer money on the AR side (a receipt's remainder, an
  // open credit memo). Deliberately NOT 2300, which is order deposits — mixing
  // them would muddy that account's tie-out.
  '2310': { name: 'Customer Credits (AR)', type: 'liability' },
  '2400': { name: 'Payouts Payable — Members', type: 'liability' },
  '2410': { name: 'Payouts Payable — Merchants', type: 'liability' },
  '2420': { name: 'Payouts Payable — Creators', type: 'liability' },
  '2430': { name: 'Stockist Payable', type: 'liability' },
  '2900': { name: 'Other Current Liabilities', type: 'liability' },
  '3000': { name: "Owner's Capital", type: 'equity' },
  '3100': { name: 'Retained Earnings', type: 'equity' },
  '3900': { name: 'Opening Balance Equity', type: 'equity' },
  '4000': { name: 'Sales — POS', type: 'income' },
  '4010': { name: 'Sales — Ecom', type: 'income' },
  '4020': { name: 'Sales — Kits & Activation', type: 'income' },
  '4030': { name: 'Sales — Consignment', type: 'income' },
  // R2: the default income account for a general invoice line (a line may
  // override it with any active income account).
  '4040': { name: 'Sales — Invoiced', type: 'income' },
  '4100': { name: 'Sales Returns', type: 'income' },
  '4200': { name: 'Shipping Income', type: 'income' },
  '4300': { name: 'Platform Fee Income', type: 'income' },
  '4310': { name: 'Payout Fee Income', type: 'income' },
  '4900': { name: 'Other Income / CD Recovery', type: 'income' },
  '5000': { name: 'Cost of Goods Sold', type: 'expense' },
  '5010': { name: 'Stockist Fulfillment Cost', type: 'expense' },
  // A PO-linked bill relieves GRNI at what the RECEIPTS credited (qty x PO
  // unit cost), so any difference between the PO price and the billed price
  // lands here instead of leaving a permanent 2010 residue (audit F05).
  '5020': { name: 'Purchase Price Variance', type: 'expense' },
  '5100': { name: 'Shrinkage & Write-offs', type: 'expense' },
  '6000': { name: 'Commission Expense — Cash', type: 'expense' },
  '6010': { name: 'Commission Expense — Scrip', type: 'expense' },
  '6020': { name: 'Top-up Bonus Expense', type: 'expense' },
  '6100': { name: 'Gateway & Bank Fees', type: 'expense' },
  '6300': { name: 'Rent', type: 'expense' },
  '6310': { name: 'Utilities', type: 'expense' },
  '6320': { name: 'Salaries & Wages', type: 'expense' },
  '6330': { name: 'Office Supplies', type: 'expense' },
  '6340': { name: 'Transportation & Delivery', type: 'expense' },
  '6350': { name: 'Marketing & Promotions', type: 'expense' },
  '6360': { name: 'Repairs & Maintenance', type: 'expense' },
  '6370': { name: 'Professional Fees', type: 'expense' },
  '6380': { name: 'Taxes & Licenses', type: 'expense' },
  '6390': { name: 'Communication', type: 'expense' },
  '6400': { name: 'Representation & Meals', type: 'expense' },
  '6410': { name: 'Miscellaneous', type: 'expense' },
  '6900': { name: 'Misc / Adjustments / Unmapped', type: 'expense' },
} as const satisfies Record<string, { name: string; type: GlAccountType }>;

export type GlAccountCode = keyof typeof GL_ACCOUNTS;

/**
 * The 12 system expense categories → their 63xx opex accounts (0169 backfill
 * + fresh-tenant seed). Keyed by the SEED name; a renamed category keeps its
 * stamped `gl_account_code`, so this map is only for seeding.
 */
export const EXPENSE_CATEGORY_GL_CODES: Record<string, GlAccountCode> = {
  Rent: '6300',
  Utilities: '6310',
  'Salaries & Wages': '6320',
  'Office Supplies': '6330',
  'Transportation & Delivery': '6340',
  'Marketing & Promotions': '6350',
  'Repairs & Maintenance': '6360',
  'Professional Fees': '6370',
  'Taxes & Licenses': '6380',
  Communication: '6390',
  'Representation & Meals': '6400',
  Miscellaneous: '6410',
};

/**
 * Where the poster mints per-funds-account bank-family sub-accounts
 * (bank/cheque/ewallet each get the next free code; tills share '1000').
 *
 * The range starts at 1060, clear of every seeded code (audit F22). It used to
 * start at 1011 and so overlapped 1020/1030/1040 — harmless only because the
 * allocator picks max(existing)+1, which silently started minting at 1041. It
 * also has to stay clear of what R2 reserves (1050 AR — Trade), and of 1045
 * AR — Stockist. `accounting.test.ts` asserts no catalog code falls inside.
 */
export const GL_BANK_SUBACCOUNT_MIN = 1060;
export const GL_BANK_SUBACCOUNT_MAX = 1099;

/** A tenant-created account code is exactly four digits, like every seeded one. */
export const GL_ACCOUNT_CODE_RE = /^\d{4}$/;

/**
 * Reserved bands a tenant may NOT create into.
 *
 * 1000–1099 is the cash/bank family: the poster mints sub-accounts there by
 * `max()+1`, so a user account inside it eats the allocator's headroom and can
 * squat the exact code a later release wants to seed — migrations 0180 and
 * 0182 both needed `DO $$ … RAISE` guards for precisely that, and a
 * user-chosen code is the same hazard with a wider blast radius.
 *
 * Catalog codes are blocked separately so the caller gets `code_reserved`
 * rather than a bare unique-constraint conflict. Beyond these, a tenant may
 * pick any four-digit code; future migrations keep the DO-guard convention.
 */
export function isReservedGlAccountCode(code: string): boolean {
  if (!GL_ACCOUNT_CODE_RE.test(code)) return false;
  if (code >= '1000' && code <= '1099') return true;
  return Object.prototype.hasOwnProperty.call(GL_ACCOUNTS, code);
}

// ── GL poster: wallet-ledger classification (P5, P12–P16, P19–P21, P26) ────

/**
 * GL codes a funds DOCUMENT may not name as its counter leg (audit F21).
 *
 * A deposit/withdrawal already books the cash side from the register row, so
 * naming another cash/bank account (1000–1099, including the lazily minted bank
 * sub-accounts), a wallet liability, or an AR control as the counter would post
 * a second, unbacked leg to an account that some OTHER subledger reconciles —
 * silently breaking `register_gl_parity` or `wallet_liability`. Only the picker
 * filtered these; the server accepted anything, and the same hole is reachable
 * from the manual-JE route by design (a manual JE is the sanctioned way to touch
 * these, under `accounting.journal.write`).
 */
/**
 * A REAL `YYYY-MM-DD`. The shape regex alone admits `2026-02-31` / `2026-13-40`,
 * which Postgres then rejects at cast time as a 22008 — a 500 where the caller
 * simply typed a bad date (audit F41, and the same hole on every date-cut
 * report route once they started accepting plain days).
 */
export function isCalendarDate(day: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

export function isBlockedFundsCounterCode(code: string): boolean {
  const n = Number(code);
  if (Number.isInteger(n) && n >= 1000 && n <= 1099) return true;
  // 1050 AR — Trade needs no entry: the range check above already covers it.
  // 2310 Customer Credits does — it is reconciled by the AR application
  // subledger (GL 2310 == Σ unapplied), so a funds document must never name it.
  return (['2100', '2120', '2130', '2310', '1030', '1040', '1045'] as const).includes(
    code as '2100' | '2120' | '2130' | '2310' | '1030' | '1040' | '1045',
  );
}

/** Wallet liability account per spendable currency kind. */
export const GL_WALLET_LIABILITY: Record<string, GlAccountCode> = {
  cash: '2100',
  gc: '2120',
  coin: '2130',
};

/**
 * `source_plan` values that are NOT comp plans. The comp-credit rule keys on
 * this set rather than enumerating kinds — plans mint kind strings freely
 * (framework riders, new plans), while the non-plan writers are a small closed
 * set. A `C` row from any sourcePlan outside this set is commission expense.
 * (The POS reversal loop's `<plan>_refund` sourcePlans are caught by the
 * `_refund` kind suffix before this set is consulted.)
 */
export const GL_NONCOMP_SOURCE_PLANS: ReadonlySet<string> = new Set([
  'checkout',
  'subscription',
  'assistant',
  'payout',
  'wallet_topup',
  'wallet_topup_bonus',
  'admin_adjust',
  'import',
]);

export type GlLedgerRowInput = {
  kind: string;
  direction: 'C' | 'D';
  currencyKind: string;
  sourcePlan: string;
  sourceEventId: string;
  metadata: Record<string, unknown> | null;
  /**
   * Was the order this row belongs to rung AT a `type='stockist'` branch? The
   * poster resolves it (this module stays pure); only wallet tenders read it.
   * See the note on the spend rule for why the counterpart changes.
   */
  stockistTill?: boolean;
};

export type GlLedgerClassification =
  | { action: 'skip' }
  | {
      action: 'post';
      /** Account CODES; the poster resolves ids. Amount = the row's amount for both legs. */
      debit: string;
      credit: string;
      /** True when the kind fell through to the 6900 honesty bucket (P26). */
      unmapped?: boolean;
    };

/**
 * The order a wallet-ledger row belongs to, when one is knowable — used by the
 * poster to apply the ORDER-family posting-start bound to order-linked rows
 * (spends, spend-refunds, POS claw-backs) instead of the row's own date.
 */
export function glOrderRefFromLedger(row: GlLedgerRowInput): string | null {
  const metaOrder = row.metadata?.['order_id'];
  if (typeof metaOrder === 'string' && metaOrder.length > 0) return metaOrder;
  const ev = row.sourceEventId;
  // `pos-refund-<paymentId>` carries a payment id, not an order id (its rows
  // carry metadata.order_id and were caught above).
  if (ev.startsWith('pos-refund-')) return null;
  if (ev.startsWith('pos-')) return ev.slice('pos-'.length);
  if (ev.startsWith('checkout-cancel-')) return ev.slice('checkout-cancel-'.length);
  if (ev.startsWith('checkout-')) return ev.slice('checkout-'.length);
  return null;
}

/**
 * Map one wallet-ledger row onto its double-entry legs (D2's typed catalog).
 *
 * PURE — no I/O, exhaustively total: anything unrecognized lands in the 6900
 * honesty bucket rather than crashing or skipping (P26), so a future plan's
 * new kind can never stall the poster or unbalance the TB. Skips are the rows
 * that are NOT company money: token/AI-credit scrip, imported history, and the
 * top-up principals a document extractor owns (cash leg comes from the funds
 * register there — mapping them here too would double-post cash).
 */
export function classifyLedgerRowForGl(row: GlLedgerRowInput): GlLedgerClassification {
  if (row.currencyKind === 'token' || row.currencyKind === 'ai_credit') return { action: 'skip' };
  if (row.metadata?.['import'] === true) return { action: 'skip' };
  if (row.kind === 'opening_balance') return { action: 'skip' };
  if (row.kind === 'wallet_topup' || row.kind === 'ai_credit_topup') return { action: 'skip' };

  const wallet = GL_WALLET_LIABILITY[row.currencyKind] ?? '2100';
  const compExpense = row.currencyKind === 'cash' ? '6000' : '6010';

  // Wallet tenders and their inverses (P5 / P20-spend).
  //
  // The counterpart is normally 2300 Customer Deposits, cleared when the order
  // completes. A sale rung AT a stockist till posts NO completion (audit F04),
  // so its 2300 would stay open forever. There the house has instead discharged
  // a member's wallet claim for goods the STOCKIST sold, so it now owes that
  // stockist the takings: 2430 Stockist Payable. That nets correctly against
  // the SAR- receivable the same sale raises (stockist owes the house wholesale
  // via 1045), leaving exactly the stockist's margin outstanding.
  const spendCounter = row.stockistTill ? '2430' : '2300';
  if (row.kind.endsWith('_spend_refund') && row.direction === 'C') {
    return { action: 'post', debit: spendCounter, credit: wallet };
  }
  if (row.kind.endsWith('_spend') && row.direction === 'D') {
    return { action: 'post', debit: wallet, credit: spendCounter };
  }

  // Orderless wallet charges — revenue at charge (subscriptions have no order).
  if (row.kind === 'subscription_charge' && row.direction === 'D') {
    return { action: 'post', debit: wallet, credit: '4900' };
  }

  // Member payout wallet legs (P15/P16) — cash only reaches here.
  if (row.kind === 'payout_hold' && row.direction === 'D') {
    return { action: 'post', debit: wallet, credit: '2400' };
  }
  if (
    (row.kind === 'payout_cancel_refund' || row.kind === 'payout_reject_refund') &&
    row.direction === 'C'
  ) {
    return { action: 'post', debit: '2400', credit: wallet };
  }

  // CD garnish (P19): earned cash redirected into the kit debt = recovery.
  if (row.kind === 'cd_debt_payment' && row.direction === 'D') {
    return { action: 'post', debit: '2100', credit: '4900' };
  }
  if (row.kind === 'cd_debt_payment_refund' && row.direction === 'C') {
    return { action: 'post', debit: '4900', credit: '2100' };
  }

  // Top-up bonuses (the principal is document-extracted; the bonus is promo).
  if (row.kind === 'wallet_topup_bonus' && row.direction === 'C') {
    return { action: 'post', debit: '6020', credit: wallet };
  }

  // Plain admin adjustments — miscellaneous, so 6900 both ways. An ATTRIBUTED
  // adjustment carries a real plan as its `sourcePlan` and falls through to the
  // comp rules below instead, in BOTH directions.
  if (row.sourcePlan === 'admin_adjust') {
    return row.direction === 'C'
      ? { action: 'post', debit: '6900', credit: wallet }
      : { action: 'post', debit: wallet, credit: '6900' };
  }

  // POS reversal loop claw-backs: inverse of the base kind's comp mapping.
  if (row.kind.endsWith('_refund') && row.direction === 'D') {
    return { action: 'post', debit: wallet, credit: compExpense };
  }

  // Comp credits (P12/P13) — every plan, every rider, by sourcePlan.
  if (row.direction === 'C' && !GL_NONCOMP_SOURCE_PLANS.has(row.sourcePlan)) {
    return { action: 'post', debit: compExpense, credit: wallet };
  }

  // …and their DEBIT counterpart (audit F15). An adjustment attributed to a
  // plan is a CLAW-BACK — `routes/adjustments.ts` decrements that plan's
  // `comp_lifetime_state` for exactly this reason — so it reverses the comp
  // expense the credit booked. Without this arm it fell through to P26's
  // honest-but-wrong 6900 fallback and read as a miscellaneous expense.
  if (row.direction === 'D' && !GL_NONCOMP_SOURCE_PLANS.has(row.sourcePlan)) {
    return { action: 'post', debit: wallet, credit: compExpense };
  }

  // P26: honest fallback. TB still foots; the unmapped-postings view surfaces it.
  return row.direction === 'C'
    ? { action: 'post', debit: '6900', credit: wallet, unmapped: true }
    : { action: 'post', debit: wallet, credit: '6900', unmapped: true };
}

/** Order-document types (Accounting Phase 2). */
export const ORDER_DOC_TYPES = ['billing_statement', 'credit_memo'] as const;
export type OrderDocType = (typeof ORDER_DOC_TYPES)[number];

/**
 * Series identity for an order document. Branch-scoped when the order has a
 * branch code (`BILL-MNL01-00001`), global `BILL`/`CM` otherwise. Pure so the
 * mint (`mintDocNumber`) and tests agree on the exact series key.
 *
 * These keys never collide with `DOC_SERIES`, whose members are all bare
 * document prefixes (CN, RTN, AR, PAY, SCN, SAR, SRTN, SCPAY, SPAY, RTS, SREF,
 * EXP, FT, JE, CUST, CRF) — `BILL` and `CM` are deliberately not among them, so
 * the global `CM` counter this function names has exactly one owner.
 */
export function orderDocSeries(
  docType: OrderDocType,
  branchCode: string | null,
): { series: string; prefix: string; pad: number } {
  const base = docType === 'billing_statement' ? 'BILL' : 'CM';
  const series = branchCode ? `${base}-${branchCode}` : base;
  return { series, prefix: `${series}-`, pad: 5 };
}

/** Payment/receipt methods for AR receipts + supplier payments. */
export const ACCOUNTING_PAYMENT_METHODS = [
  'cash',
  'bank_transfer',
  'gcash',
  'check',
  'other',
] as const;
export type AccountingPaymentMethod = (typeof ACCOUNTING_PAYMENT_METHODS)[number];

/** Payment sources for expenses (where the money left from). */
export const EXPENSE_PAYMENT_SOURCES = ['cash', 'bank', 'gcash', 'check', 'other'] as const;
export type ExpensePaymentSource = (typeof EXPENSE_PAYMENT_SOURCES)[number];

/**
 * Decompose a VAT-INCLUSIVE amount into its VATable base and VAT portion.
 * `vatable = round(total / (1 + pct/100), 2)`; `vat = total − vatable` — the
 * VAT is the REMAINDER so the two legs always sum exactly to the total (no
 * rounding drift; a future balanced journal entry can never be off by a
 * centavo). Mirrors the preview billing page's arithmetic.
 */
export function decomposeVatInclusive(
  total: Decimal.Value,
  pct: Decimal.Value = 12,
): { vatable: Decimal; vat: Decimal } {
  const t = new Decimal(total);
  const divisor = new Decimal(1).plus(new Decimal(pct).div(100));
  const vatable = t.div(divisor).toDecimalPlaces(2);
  return { vatable, vat: t.minus(vatable) };
}

/**
 * The 3-code tax registry (Accounting Phase 2) — the classification the PH
 * 2550Q quarterly VAT return needs: VATable (12% by default), VAT-exempt, and
 * zero-rated. Product-level classification; house order lines snapshot it at
 * checkout. A NULL snapshot ("untagged") means the line was recorded before
 * `house_vat_enabled`, or belongs to a merchant/creator sub-order — it is NOT
 * the same as exempt and is surfaced as its own bucket.
 */
export const TAX_CODES = ['vatable', 'exempt', 'zero_rated'] as const;
export type TaxCode = (typeof TAX_CODES)[number];

export type VatLineBuckets = {
  vatable: Decimal;
  vat: Decimal;
  exempt: Decimal;
  zeroRated: Decimal;
  untagged: Decimal;
};

/**
 * Bucket one order line's VAT-INCLUSIVE total by its checkout snapshot — THE
 * single decomposition implementation shared by the order-document service and
 * the vat-sales report (any second implementation is the drift this helper
 * exists to prevent). `taxCode` null → untagged; 'vatable' with `vatEnabled` →
 * `decomposeVatInclusive(total, pct)` (VAT = remainder, legs sum exactly);
 * 'vatable' without `vatEnabled` (defensive — the snapshot never writes that
 * combination) → vatable base at 0 VAT; 'exempt'/'zero_rated' → their bucket,
 * VAT 0. Unknown codes fall back to untagged rather than throwing so a report
 * can never crash on a future code.
 */
export function bucketVatLine(args: {
  total: Decimal.Value;
  vatEnabled: boolean;
  vatPct: Decimal.Value;
  taxCode: string | null;
}): VatLineBuckets {
  const zero = new Decimal(0);
  const out: VatLineBuckets = {
    vatable: zero,
    vat: zero,
    exempt: zero,
    zeroRated: zero,
    untagged: zero,
  };
  const total = new Decimal(args.total);
  switch (args.taxCode) {
    case 'vatable': {
      if (args.vatEnabled) {
        const { vatable, vat } = decomposeVatInclusive(total, args.vatPct);
        out.vatable = vatable;
        out.vat = vat;
      } else {
        out.vatable = total;
      }
      return out;
    }
    case 'exempt':
      out.exempt = total;
      return out;
    case 'zero_rated':
      out.zeroRated = total;
      return out;
    default:
      out.untagged = total;
      return out;
  }
}

/**
 * Allocate a 2-decimal amount across weights pro-rata, exactly. Each share is
 * rounded to 2dp; the accumulated rounding residue lands on the LAST non-zero
 * weight (remainder method — same doctrine as `decomposeVatInclusive`), so the
 * shares always sum to `amount` to the centavo. Used to spread an order-level
 * manager discount across its lines for the VAT basis (a discount granted at
 * the time of sale reduces the VAT base). Zero/negative amount or all-zero
 * weights → all-zero shares.
 */
export function allocateProRata(amount: Decimal.Value, weights: Decimal.Value[]): Decimal[] {
  const amt = new Decimal(amount);
  const w = weights.map((x) => new Decimal(x));
  const totalWeight = w.reduce((s, x) => s.plus(x), new Decimal(0));
  if (amt.lte(0) || totalWeight.lte(0)) return w.map(() => new Decimal(0));
  const lastNonZero = w.reduce((idx, x, i) => (x.gt(0) ? i : idx), -1);
  let allocated = new Decimal(0);
  return w.map((x, i) => {
    if (i === lastNonZero) return amt.minus(allocated);
    const share = amt.mul(x).div(totalWeight).toDecimalPlaces(2);
    allocated = allocated.plus(share);
    return share;
  });
}

/**
 * Frozen print snapshot stored in `order_documents.payload` (Accounting
 * Phase 2). The admin renders EXCLUSIVELY from this — a later product rename
 * or price edit never changes an issued document. All money is `.toFixed(2)`
 * strings. Titles never use the BIR-registered statutory names ("Sales
 * Invoice" / "Official Receipt") — that naming is reserved for the separate
 * compliance track.
 */
export type OrderDocumentLine = {
  sku: string | null;
  name: string;
  variantName: string | null;
  qty: number;
  unitPrice: string;
  /** `round2(unitPrice × qty − line coupon discount)` — the printed Amount. */
  lineTotal: string;
  taxCode: TaxCode | null;
  vatEnabled: boolean;
  vatPct: string;
  /**
   * Decomposition of the line's VAT BASIS = lineTotal − this line's pro-rata
   * share of the order-level manager discount (a discount granted at sale
   * reduces the VAT base). vatable + vat = that basis exactly.
   */
  vatable: string;
  vat: string;
};

export type OrderDocumentPayload = {
  version: 1;
  /** 'Billing Statement / Order Invoice' | 'Credit Memo' — never statutory names. */
  title: string;
  docType: OrderDocType;
  docNo: string;
  orderId: string;
  orderCode: string;
  paymentCurrency: string;
  issuedAt: string;
  issuedByName: string | null;
  company: {
    name: string | null;
    address: string | null;
    tin: string | null;
    phone: string | null;
    branchName: string | null;
    branchCode: string | null;
  };
  buyer: {
    name: string | null;
    email: string | null;
    phone: string | null;
    addressLines: string[];
  };
  lines: OrderDocumentLine[];
  /**
   * The printed arithmetic foots EXACTLY at 2dp, by construction: figures
   * live in rounded-display space (goods = Σ round2(line amounts) + coupon;
   * coupon = Σ round2(line coupons)), the manager discount is allocated
   * pro-rata into the per-line VAT bases (so Σ(vatable+vat+exempt+zeroRated+
   * untagged) = goods − coupon − managerDiscount), and any residue that
   * independent 2dp rounding of 4dp stored values leaves over is carried in
   * `rounding`, giving goods − coupon − managerDiscount + shipping +
   * customFees − rounding = total to the centavo. The Phase 4 GL still posts
   * from order totals and stays the book of record.
   */
  totals: {
    goods: string;
    vatable: string;
    vat: string;
    exempt: string;
    zeroRated: string;
    untagged: string;
    coupon: string;
    managerDiscount: string;
    shipping: string;
    customFees: string;
    /**
     * Display-space residue: goods − coupon − managerDiscount + shipping +
     * customFees − total, i.e. what independent 2dp rounding of 4dp stored
     * values leaves over (usually "0.00", occasionally ±0.01). Carried as its
     * own honest row instead of being folded into the discount, so the
     * printed sheet always foots to the exact charged total.
     */
    rounding: string;
    total: string;
  };
  /** Credit memo only — who/when/why from the order's refund history. */
  refund?: {
    refundedAt: string;
    refundedByName: string | null;
    note: string | null;
  };
  footer: string;
};

/** AP/AR aging buckets, oldest last. `current` = not yet past due. */
export const AGING_BUCKETS = ['current', '1-30', '31-60', '61-90', '90+'] as const;
export type AgingBucket = (typeof AGING_BUCKETS)[number];

/**
 * Whole days past due between two DATE-ONLY strings (`YYYY-MM-DD`), positive
 * when `asOf` is after `due`. Date-only in, so the caller decides the timezone
 * (render both in the tenant's business timezone before calling) and this
 * stays a pure calendar diff.
 */
export function daysPastDue(due: string, asOf: string): number {
  const d = Date.UTC(
    Number(due.slice(0, 4)),
    Number(due.slice(5, 7)) - 1,
    Number(due.slice(8, 10)),
  );
  const a = Date.UTC(
    Number(asOf.slice(0, 4)),
    Number(asOf.slice(5, 7)) - 1,
    Number(asOf.slice(8, 10)),
  );
  return Math.round((a - d) / 86_400_000);
}

/**
 * The due DAY for a document dated `docDay` under `term`, as `YYYY-MM-DD`.
 *
 * Date-only in and out, the same contract as `daysPastDue` above: the caller
 * renders the document date in the tenant's business timezone first (services
 * use `businessDate`) and materializes the result back the same way, so this
 * stays pure calendar arithmetic with no DST edge cases of its own. That is
 * deliberately unlike AP's `defaultDueDate`, which adds milliseconds and so
 * drifts by an hour across a DST boundary.
 *
 * `net` = day + days. `eom` = day + days, then the last day of THAT month —
 * so `eom` with days=0 is month-end, and days=30 on Jan 15 lands Feb 28/29.
 */
export function computeDueDate(
  docDay: string,
  term: { days: number; kind: PaymentTermKind },
): string {
  const shifted = new Date(
    Date.UTC(
      Number(docDay.slice(0, 4)),
      Number(docDay.slice(5, 7)) - 1,
      Number(docDay.slice(8, 10)) + (term.days || 0),
    ),
  );
  // Day 0 of month N+1 is the last day of month N.
  const due =
    term.kind === 'eom'
      ? new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0))
      : shifted;
  return due.toISOString().slice(0, 10);
}

/** Bucket a balance by how many whole days past due it is. */
export function agingBucket(due: string, asOf: string): AgingBucket {
  const days = daysPastDue(due, asOf);
  if (days <= 0) return 'current';
  if (days <= 30) return '1-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
}
